import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const targetName = (it) => it.partner_name || it.prospect_name || it.resolved_agency_name || it.agency_name || "—";

export default function OverdueVisitsDialog({ open, onOpenChange, items = [], onDone }) {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows((items || []).map((it) => ({ ...it, esito: null, notes: "" })));
    setErrors({});
  }, [items]);

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const submit = async () => {
    const toSend = rows.filter((r) => r.esito);
    if (!toSend.length) { toast.info("Segna l'esito di almeno una visita"); return; }
    setSaving(true);
    try {
      const r = await api.post("/activities/bulk-esito", {
        items: toSend.map((x) => ({ id: x.id, esito: x.esito, notes: x.notes || "" })),
      });
      const { updated_ids = [], errors: errs = [] } = r.data || {};
      const errMap = {};
      errs.forEach((e) => { errMap[e.id] = e.error; });
      setErrors(errMap);
      const remaining = rows.filter((x) => !updated_ids.includes(x.id));
      setRows(remaining);
      if (updated_ids.length) toast.success(`${updated_ids.length} visite aggiornate`);
      if (errs.length) toast.error(`${errs.length} da correggere`);
      onDone?.();
      if (!remaining.length) onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="overdue-visits-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Visite passate da chiudere
            <Badge className="bg-amber-500 text-black">{rows.length}</Badge>
          </DialogTitle>
          <DialogDescription>
            Queste visite sono ancora "pianificate" ma la data è passata. Indica per ognuna se è stata <strong>svolta</strong> (con nota) o <strong>non svolta</strong>, poi conferma.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid={`overdue-row-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{targetName(r)}</div>
                    <div className="text-[11px] text-muted-foreground">{r.date}{r.time ? ` · ${r.time}` : ""}{r.owner_name ? ` · ${r.owner_name}` : ""}</div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <Button type="button" size="sm" variant={r.esito === "svolta" ? "default" : "outline"}
                      className={r.esito === "svolta" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                      onClick={() => setRow(r.id, { esito: "svolta" })} data-testid={`overdue-svolta-${r.id}`}>
                      <Check className="mr-1 h-3.5 w-3.5" /> Svolta
                    </Button>
                    <Button type="button" size="sm" variant={r.esito === "non_svolta" ? "default" : "outline"}
                      className={r.esito === "non_svolta" ? "bg-rose-600 hover:bg-rose-700" : ""}
                      onClick={() => setRow(r.id, { esito: "non_svolta" })} data-testid={`overdue-nonsvolta-${r.id}`}>
                      <X className="mr-1 h-3.5 w-3.5" /> Non svolta
                    </Button>
                  </div>
                </div>
                {r.esito === "svolta" && (
                  <div className="mt-2">
                    <Textarea rows={2} value={r.notes} onChange={(e) => setRow(r.id, { notes: e.target.value })}
                      placeholder="Nota obbligatoria: cosa è emerso, esito, prossimi passi…"
                      className={errors[r.id] ? "border-rose-500" : ""}
                      data-testid={`overdue-note-${r.id}`} />
                  </div>
                )}
                {errors[r.id] && (
                  <div className="mt-1 text-[11px] text-rose-400" data-testid={`overdue-error-${r.id}`}>✗ {errors[r.id]}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="overdue-later-btn">Più tardi</Button>
          <Button onClick={submit} disabled={saving} data-testid="overdue-confirm-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
